<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Gifts List</h1>
        <span class="page-subtitle">Manage and track all gift vouchers</span>
      </div>
      <div class="page-header__right">
        <button class="btn-export" @click="exportToCSV" v-if="items && items.length" title="Export to CSV">
          <i class="fa fa-download"></i> Export CSV
        </button>
      </div>
    </div>

    <!-- ── Filters ── -->
    <div class="filter-panel">
      <div class="filter-panel__row">

        <div class="filter-field filter-field--grow">
          <label class="filter-label">Search</label>
          <div class="input-icon-wrap">
            <i class="fa fa-search input-icon"></i>
            <input
                placeholder="Search by customer, gift ID..."
                v-model="inputFilters.q"
                class="dd-input dd-input--icon"
                v-on:keyup.enter="filter"
            />
          </div>
        </div>

        <div class="filter-field">
          <label class="filter-label">Purchase Date</label>
          <date-picker
              v-model="inputFilters.purchase_date"
              locale="en"
              format="YYYY-MM-DD"
              range
              clearable
              color="#f42f4b"
          />
        </div>

        <div class="filter-field filter-field--sm">
          <label class="filter-label">Gift Type</label>
          <select v-model="inputFilters.type" class="dd-input">
            <option value="">All</option>
            <option value="evoucher">E-Voucher</option>
            <option value="giftbox">Giftbox</option>
          </select>
        </div>

        <div class="filter-field filter-field--sm">
          <label class="filter-label">Status</label>
          <select v-model="inputFilters.status" class="dd-input">
            <option value="">All</option>
            <option value="1">Not Used</option>
            <option value="0">Used</option>
            <option value="2">Exchanged</option>
            <option value="3">Canceled</option>
            <option value="expired">Expired</option>
          </select>
        </div>

        <div class="filter-field filter-field--action">
          <label class="filter-label" style="visibility:hidden">Go</label>
          <div class="action-btns-row">
            <button class="btn-filter" @click="filter">
              <i class="fa fa-search"></i> Filter
            </button>
            <button class="btn-reset" @click="clearFilters" title="Reset filters">
              <i class="fa fa-undo"></i>
            </button>
          </div>
        </div>

      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>Gift ID</th>
            <th>Customer</th>
            <th>Country</th>
            <th>Experience / City</th>
            <th>Purchase Date</th>
            <th>Type</th>
            <th>Status</th>
          </tr>
          </thead>
          <tbody>
          <router-link
              tag="tr"
              :to="{ name: 'gift.show', params: { id: gift.id } }"
              v-for="gift in items"
              :key="gift.id"
              class="dd-table__row"
          >
            <td><span class="order-id">#{{ gift.id }}</span></td>
            <td>
              <div class="customer-info">
                <div class="customer-avatar">{{ (gift.customer_name || '?')[0].toUpperCase() }}</div>
                <span class="customer-name">{{ gift.customer_name || '—' }}</span>
              </div>
            </td>
            <td>
                <span class="country-cell" v-if="gift.customer_country">
                  {{ gift.customer_country }}
                </span>
              <span class="text-muted" v-else>—</span>
            </td>
            <td>
              <div class="exp-name">{{ gift.title }}</div>
              <div class="exp-city" v-if="gift.city">
                <i class="fa fa-map-marker"></i> {{ gift.city }}
              </div>
            </td>
            <td class="date-cell">{{ formatDate(gift.created_at) || '—' }}</td>
            <td>
                <span :class="['type-badge', gift.type === 'evoucher' ? 'type-badge--voucher' : 'type-badge--box']">
                  <i :class="gift.type === 'evoucher' ? 'fa fa-envelope' : 'fa fa-gift'"></i>
                  {{ gift.type === 'evoucher' ? 'E-Voucher' : 'Giftbox' }}
                </span>
            </td>
            <td v-html="getGiftStatus(gift.status)"></td>
          </router-link>

          <tr v-if="items && items.length === 0">
            <td colspan="7" class="empty-state">
              <i class="fa fa-gift"></i>
              <div>No gifts found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrap">
        <div class="pagination-info">
          Showing {{ items ? items.length : 0 }} of {{ totalItems }} gifts
        </div>
        <b-pagination
            v-model="page"
            :total-rows="totalItems"
            :per-page="perPage"
            last-number
            class="dd-pagination"
        ></b-pagination>
      </div>
    </div>

  </div>
</template>

<script>
import { config } from "../helpers";

export default {
  name: "Gifts",
  data() {
    return {
      url: "",
      items: [],
      inputFilters: {
        q: null,
        type: "",
        status: "",
        purchase_date: [],
      },
      isFiltered: false,
      filters: {},
      page: 1,
      lastPage: 1,
      totalItems: 0,
      perPage: 0,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/gifts";
    this.loadData();
  },

  methods: {
    loadData() {
      let myData = { page: this.page };
      if (this.isFiltered) myData = { ...myData, ...this.filters };
      axios.get(this.url, { params: myData }).then((response) => {
        this.items      = response.data.data;
        this.page       = response.data.meta.current_page;
        this.lastPage   = response.data.meta.last_page;
        this.totalItems = response.data.meta.total;
        this.perPage    = response.data.meta.per_page;
      });
    },

    filter() {
      this.page       = 1;
      this.isFiltered = true;
      const filters   = {};

      if (this.inputFilters.q)      filters.q           = this.inputFilters.q;
      if (this.inputFilters.status) filters.status       = this.inputFilters.status;
      if (this.inputFilters.type)   filters.gift_format  = this.inputFilters.type;

      if (this.inputFilters.purchase_date[0]) {
        if (this.inputFilters.purchase_date.length === 1) {
          this.inputFilters.purchase_date[1] = this.formatDate(new Date());
        }
        filters.purchase_date = this.inputFilters.purchase_date;
      }

      this.filters = filters;
      this.loadData();
    },

    clearFilters() {
      this.inputFilters = { q: null, type: "", status: "", purchase_date: [] };
      this.isFiltered   = false;
      this.filters      = {};
      this.loadData();
    },

    exportToCSV() {
      let myData = { page: this.page };
      if (this.isFiltered) myData = { ...myData, ...this.filters };
      axios.get(this.url + "/export", { params: myData }).then((response) => {
        const url  = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href  = url;
        link.setAttribute("download", "gifts.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
    },

    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,"0")}`;
    },

    getGiftStatus(status) {
      const map = {
        Dated:     ["status-badge status-badge--green", "Dated"],
        Open:      ["status-badge status-badge--blue",  "Open"],
        Exchanged: ["status-badge status-badge--amber", "Exchanged"],
        Canceled:  ["status-badge status-badge--red",   "Canceled"],
        Expired:   ["status-badge status-badge--gray",  "Expired"],
      };
      const [cls, label] = map[status] ?? ["status-badge status-badge--gray", status];
      return `<span class="${cls}">${label}</span>`;
    },
  },

  watch: {
    page() { this.loadData(); },
  },
};
</script>

<style scoped>
.page-wrap   { display: flex; flex-direction: column; gap: 16px; }

.page-header { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title  { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }

.btn-export {
  height: 36px; padding: 0 14px;
  background: var(--bg-hover); border: 1px solid var(--border);
  border-radius: 8px; color: var(--text-2); font-size: 13px;
  cursor: pointer; display: flex; align-items: center; gap: 6px;
  transition: border-color .15s, color .15s;
}
.btn-export:hover { border-color: var(--accent); color: var(--accent); }

/* Filter */
.filter-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.1rem 1.25rem; }
.filter-panel__row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-field         { display: flex; flex-direction: column; min-width: 160px; }
.filter-field--grow   { flex: 1; }
.filter-field--sm     { min-width: 130px; max-width: 160px; }
.filter-field--action { min-width: auto; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; }
.dd-input--icon { padding-left: 32px; }

.action-btns-row { display: flex; gap: 8px; }
.btn-filter { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-filter:hover { opacity: .88; }
.btn-reset  { height: 36px; width: 36px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s, color .15s; }
.btn-reset:hover { border-color: var(--border-2); color: var(--text-1); }

/* Table */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); white-space: nowrap; text-align: left; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row { cursor: pointer; transition: background .12s; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table__row:last-child td { border-bottom: none; }

.order-id { font-weight: 500; color: var(--text-1); }

.customer-info   { display: flex; align-items: center; gap: 9px; }
.customer-avatar { width: 28px; height: 28px; background: rgba(244,47,75,.12); color: var(--accent); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; }
.customer-name   { font-size: 13px; font-weight: 500; color: var(--text-1); }

.country-cell { font-size: 13px; color: var(--text-2); }
.text-muted   { color: var(--text-3); }

.exp-name  { font-weight: 500; color: var(--text-1); margin-bottom: 2px; }
.exp-city  { font-size: 11px; color: var(--text-3); }
.exp-city i { margin-right: 3px; }

.date-cell { font-size: 12px; color: var(--text-3); white-space: nowrap; }

/* Type badge */
.type-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 9px; border-radius: 20px;
  font-size: 11px; font-weight: 500; white-space: nowrap;
}
.type-badge--voucher { background: rgba(129,140,248,.12); color: #818cf8; border: 1px solid rgba(129,140,248,.25); }
.type-badge--box     { background: rgba(245,158,11,.12);  color: #f59e0b; border: 1px solid rgba(245,158,11,.25); }

/* Empty state */
.empty-state { text-align: center; padding: 3rem 1rem !important; color: var(--text-3); }
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }

/* Pagination */
.pagination-wrap { display: flex; align-items: center; justify-content: space-between; padding: .75rem 1.25rem; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 8px; }
.pagination-info { font-size: 12px; color: var(--text-3); }
.dd-pagination >>> .page-link { background: var(--bg-hover); border-color: var(--border); color: var(--text-2); font-size: 13px; }
.dd-pagination >>> .page-item.active .page-link { background: var(--accent); border-color: var(--accent); color: #fff; }
.dd-pagination >>> .page-link:hover { background: var(--bg-hover); border-color: var(--accent); color: var(--accent); }

@media (max-width: 768px) {
  .filter-panel__row { flex-direction: column; }
  .filter-field, .filter-field--sm, .filter-field--grow { width: 100%; max-width: 100%; }
}
</style>